/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren, OnChanges, AfterViewInit,
} from '@angular/core';
import { Dropdown } from 'primeng/dropdown';

import { TranslationService } from '../../common/translation.service';

/**
 * Component ( Prototype ) for our aero-dropdown.
 */
@Component({
  selector: 'aero-dropdown',
  templateUrl: './aero-dropdown.component.html',
  standalone: false,
})
export class AeroDropdownComponent implements OnInit, OnChanges, AfterViewInit {
  /**
   * Options for the ng model bounded data.
   */
  @Input() choices : any = [];

  /**
   * Specifies the type of data in 'choices' array. Possible values are 'string' and 'object'.
   * If this value is not provided, it is assumed that the choices would be of array of objects with
   * 'display' and 'value' as the object keys such as OrganizationController.sessionTimeoutOptions.
   */
  @Input() choicesType? : string;

  /**
   * Only to be used when AeroDropdownComponent.choicesType is 'object'.
   * It is used to get the value from the choices object and use as the labels for dropdown options.
   * For example: choices is array of object type and we want to use one of the object field as the label for dropdown items,
   * then we can provide the name of the object field here.
   *
   * [{
   *    'name':'Joe',
   *    'age': 25,
   *    ...
   * }, {
   *    'name':'Joy',
   *    'age': 30,
   *    ...
   * }
   * ]
   *
   * Here if pass 'name' as 'choicesField'. Then we would have a dropdown with 'Joe' and 'Joy' as dropdown items.
   */
  @Input() choicesField? : string;

  /**
   * Boolean flag to determine whether choices label are to be translated.
   */
  @Input() translateChoices? : boolean;

  /**
   * Unique identifier of the accessible input element.
   */
  @Input() inputElementId : any;

  /**
   * Options arrays list needed so that the HTML template could render them. Naming needed for primeng dropdown
   * component.
   */
  public options : Array<any> = [];

  /**
   * Specified ARIA Label code for the hidden accessible element appended by primeng for already processed text.
   */
  @Input() ariaLabel? : string;

  /**
   * Defines a string that labels the filter input.
   */
  @Input() ariaFilterLabel? : string;

  /**
   * Placeholder for input field.
   */
  @Input() placeholder? : string;

  /**
   * Specifies that an input field must be filled out before submitting the form.
   */
  @Input() required? : boolean = false;

  /**
   * Specifies whether we need search/filter capability in the dropdown.
   */
  @Input() enableSearch? : boolean = false;

  /**
   * Icon class of the dropdown icon. Default is the font awesome icon : fas fa-chevron-down
   */
  @Input() dropDownIcon? : string = 'fas fa-chevron-down';

  /**
   * Whether to display the first item as the label if no placeholder is defined and value is null.
   */
  @Input() autoDisplayFirst? : boolean = false;

  /**
   * Style class of the element.
   */
  @Input() styleClass? : string;

  /**
   * Selected option from the dropdown
   */
  @Input() selectedOption?: any;

  /**
   * Form field this element is bind to. Used to validate the form field.
   */
  @Input() formField?: any;

  /**
   * This field use to filter on field name.
   */
  @Input() searchFilterBy?: any;

  /**
   * Field name to show label.
   */
  @Input() searchOptionLabel?: any;

  /**
   * Event Emitter for communicating with the parent publish panel.
   */
  @Output() communicating: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Dropdown element rendered on dom.
   */
  @ViewChildren(Dropdown) dropdowns: QueryList<Dropdown>;

  /**
   * Dropdown element created.
   */
  private dropdown : Dropdown;

  previousValue : any;

  /**
   * Default Constructor.
   *
   * @param $translate
   */
  constructor(private $translate : TranslationService) {}

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    this.handleCreatingInputPlaceholder();
    this.handleAriaLabel();
    this.styleClass = (this.styleClass) ? `${this.styleClass} aero-dropdown` : 'aero-dropdown';
  }

  /**
   * A lifecycle hook that is called after any data-bound properties is changed of a directive.
   * To handle any additional changes to the component.
   * @param changes in the component
   */
  ngOnChanges(changes: any) {
    if (changes.choices) {
      this.handleCreatingChoices();
    }
  }

  /**
   * A callback method that is invoked immediately after Angular has completed initialization of a component's view.
   */
  ngAfterViewInit(): void {
    this.dropdown = this.dropdowns.first;
  }

  /**
   * Helper method to handle creating the array of choices for the drop down.
   *
   * Need to configure the passed choices with a label and a value so that the primeng dropdown component can render
   * them.
   */
  private handleCreatingChoices(): void {
    if (!this.choices) return;
    this.options = [];
    this.choices.forEach((option) => {
      let optionObject;
      if (this.choicesType === 'string') {
        optionObject = {
          label: this.translateOptionLabel(option),
          value: option,
        };
      } else if (this.choicesType === 'object' && this.choicesField) {
        optionObject = {
          label: this.translateOptionLabel(option[this.choicesField]),
          value: option,
        };
      } else {
        optionObject = {
          label: this.translateOptionLabel(option.display),
          value: (option.value) ? option.value : option.display,
        };
      }
      this.options.push(optionObject);
    });
    this.previousValue = this.selectedOption;
  }

  /**
   * Translate the option label if translateChoices flag is true.
   * @param label to be translated
   */
  private translateOptionLabel(label: string): string {
    return (this.translateChoices) ? this.$translate.translateInstant(label) : label;
  }

  /**
   * Handle adding the aria label to the hidden accessible input.
   */
  private handleAriaLabel() : void {
    if (this.ariaFilterLabel) {
      this.ariaFilterLabel = this.$translate.translateInstant(this.ariaFilterLabel);
    }
    // Set the correct aria label on the unique input associated with the drop down.
    setTimeout(() => {
      jQuery(`#${this.inputElementId}`)
        .removeAttr('readonly aria-label');
    });
  }

  /**
   * Helper method to handle creating and configuring the input placeholder.
   */
  private handleCreatingInputPlaceholder(): void {
    if (this.placeholder) {
      this.placeholder = this.$translate.translateInstant(this.placeholder);
    }
  }

  onChange(event: any): void {
    this.selectedOption = event.value;
    if (this.formField) {
      this.formField.$setDirty();
    }
  }

  /**
   * Callback to invoke when dropdown overlay gets hidden
   * @param event
   */
  handleOnHideEvent(): void {
    this.styleClass = this.styleClass.replace(' aero-validation', '');
    this.dropDownIcon = 'fas fa-chevron-down';
    if (this.formField && !this.selectedOption) {
      this.formField.$invalid = true;
      this.styleClass += ' aero-validation';
    }
    this.communicating.emit({ selectedOption: this.selectedOption });
  }

  /**
   * Callback to invoke when dropdown overlay gets visible.
   * @param event
   */
  handleOnShowEvent(): void {
    this.dropDownIcon = 'fas fa-chevron-up';
    if (this.formField) {
      this.formField.$touched = true;
      if (!this.previousValue) {
        this.formField.$invalid = false;
      }
    }
  }

  /**
   * Callback to handle key down events for the dropdown.
   * @param event
   */
  handleKeydownEvent(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        if (!this.dropdown.overlayVisible) {
          this.dropdown.show();
        }
        event.preventDefault();
        break;

      case 'Home':
        if (this.dropdown.overlayVisible) {
          this.dropdown.selectedOption = this.options[0];
        }
        event.preventDefault();
        break;

      case 'End':
        if (this.dropdown.overlayVisible) {
          this.dropdown.selectedOption = this.options[this.options.length - 1];
        }
        event.preventDefault();
        break;
    }
  }

  /**
   * Callback to handle blur event for the dropdown.
   */
  handleOnBlur() {
    this.styleClass = this.styleClass.replace(' aero-validation', '');
    if (this.formField && !this.selectedOption) {
      this.formField.$invalid = true;
      this.formField.$touched = true;
      this.styleClass += ' aero-validation';
    }
  }

  /**
   * Method to clear the error message from field.
   */
  clearError() {
    if (this.formField) {
      this.formField.$invalid = false;
      this.formField.$touched = false;
      this.styleClass = 'aero-dropdown';
    }
  }
}
